import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { ScheduleTabs } from "@/components/schedule/ScheduleTabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ClientPicker } from "@/components/clients/ClientPicker";
import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  Link2,
  Loader2,
  MapPin,
  MoreHorizontal,
  Pencil,
  Plus,
  Rows3,
  UserPlus,
  X,
} from "lucide-react";
import {
  getUpcomingCalendarEvents,
  getGoogleCalendarAuthUrl,
  linkCalendarEvent,
  dismissCalendarEvent,
  addClientFromCalendarEvent,
  type UpcomingEvent,
} from "@/lib/google-calendar.functions";
import { listLocations, getForecastsForCoach } from "@/lib/weather.functions";
import { WeatherIcon, type WeatherSeverity } from "@/components/calendar/WeatherIcon";
import {
  NewBookingDialog,
  type NewBookingSeed,
} from "@/components/calendar/NewBookingDialog";
import { toast } from "sonner";

const VIEW_STORAGE_KEY = "cq:schedule.view";
const HOUR_HEIGHT = 64;
const HOUR_START = 7; // 7 AM
const HOUR_END = 20; // 8 PM
const HOURS = Array.from({ length: HOUR_END - HOUR_START + 1 }, (_, i) => HOUR_START + i);
const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;
const DAY_FULL = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

type ScheduleView = "week" | "list";

type ForecastLite = {
  google_event_id: string | null;
  weather_code: number | null;
  temp_f: number | null;
  precip_prob: number | null;
  wind_mph: number | null;
  severity: WeatherSeverity;
  flag_reason: string | null;
  severe_alert: { event?: string; headline?: string } | null;
};

type ScheduleEvent = {
  id: string;
  source: UpcomingEvent;
  day: number; // 0 = Mon, ..., 6 = Sun
  startHr: number;
  endHr: number;
  durationMin: number;
  startDate: Date;
  endDate: Date;
  clientId: string | null;
  clientName: string;
  clientEmail: string | null;
  sessionId: string | null;
  bucket: UpcomingEvent["bucket"];
  court: string;
  skills?: string | null;
  now: boolean;
  done: boolean;
  needsReview: boolean;
  attendeeName: string | null;
  attendeeEmail: string | null;
  attendeePhone: string | null;
  isCalendly: boolean;
  title: string;
  /** True when this came from the CoachQuick booking page, not Google. */
  isBooking?: boolean;
  /** True when this is a CoachQuick clinic (group event). */
  isClinic?: boolean;
  clinicId?: string;
};

type EventActions = {
  logSession: () => void;
  openRecap: () => void;
  reschedule: () => void;
  addClient: () => void;
  linkExisting: () => void;
  dismiss: () => void;
  /** CoachQuick booking-page reservations only. */
  cancelBooking: () => void;
};

const AVATAR_PALETTE = [
  "bg-[#9fe870] text-[#054d28]",
  "bg-[#fdba74] text-[#7c2d12]",
  "bg-[#fcd34d] text-[#78350f]",
  "bg-[#7dd3fc] text-[#075985]",
  "bg-[#f0abfc] text-[#701a75]",
  "bg-[#a5b4fc] text-[#312e81]",
  "bg-[#fda4af] text-[#881337]",
];

function initialsOf(name: string) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function paletteFor(seed: string) {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function startOfWeek(date: Date, offsetWeeks: number) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const dow = d.getDay(); // 0 Sun .. 6 Sat
  const monShift = dow === 0 ? -6 : 1 - dow;
  d.setDate(d.getDate() + monShift + offsetWeeks * 7);
  return d;
}

function addDays(d: Date, n: number) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function isoDay(d: Date) {
  return d.toISOString().slice(0, 10);
}

function fractionalHour(d: Date) {
  return d.getHours() + d.getMinutes() / 60;
}

function dayIndex(d: Date) {
  const dow = d.getDay();
  return dow === 0 ? 6 : dow - 1; // Mon=0 ... Sun=6
}

function fmtHourLabel(hr: number) {
  if (hr === 0) return "12 AM";
  if (hr === 12) return "12 PM";
  if (hr < 12) return `${hr} AM`;
  return `${hr - 12} PM`;
}

function fmtTimeOfDay(d: Date) {
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function fmtDuration(min: number) {
  if (min < 60) return `${min} min`;
  if (min % 60 === 0) return `${min} min`;
  return `${min} min`;
}

function locShort(name: string | null | undefined) {
  if (!name) return "";
  const cleaned = name.replace(/[^\w\s·]/g, "").trim();
  const word = cleaned.split(/\s+/)[0] ?? "";
  return word.slice(0, 5);
}

function nameFromEvent(ev: UpcomingEvent): string {
  if (ev.matchedClient) return ev.matchedClient.name;
  if (ev.attendee?.name) return ev.attendee.name;
  return ev.title || "Lesson";
}

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined;
}

function CalendarPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const fetchEvents = useServerFn(getUpcomingCalendarEvents);
  const fetchAuthUrl = useServerFn(getGoogleCalendarAuthUrl);
  const fetchLocs = useServerFn(listLocations);
  const fetchForecasts = useServerFn(getForecastsForCoach);
  const linkEvt = useServerFn(linkCalendarEvent);
  const dismissEvt = useServerFn(dismissCalendarEvent);
  const addClientEvt = useServerFn(addClientFromCalendarEvent);

  const [view, setView] = useState<ScheduleView>("week");
  const [weekOffset, setWeekOffset] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const [reviewOpen, setReviewOpen] = useState<{
    ev: ScheduleEvent;
    tab: "create" | "link";
  } | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ScheduleEvent | null>(null);
  const [bookingSeed, setBookingSeed] = useState<NewBookingSeed | null>(null);

  const cancelBookingMut = useMutation({
    mutationFn: async (bookingId: string) => {
      const { error } = await supabase
        .from("coach_bookings")
        .update({
          status: "cancelled",
          cancelled_at: new Date().toISOString(),
          cancelled_by: "coach",
        } as never)
        .eq("id", bookingId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Booking cancelled");
      qc.invalidateQueries({ queryKey: ["calendar-bookings"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to cancel"),
  });

  const invalidateEvents = () => qc.invalidateQueries({ queryKey: ["calendar-events", 28] });

  const linkMut = useMutation({
    mutationFn: async (vars: { googleEventId: string; clientId: string }) => {
      const headers = await authHeaders();
      return linkEvt({ data: vars, headers });
    },
    onSuccess: () => {
      toast.success("Linked to client");
      invalidateEvents();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to link"),
  });
  const dismissMut = useMutation({
    mutationFn: async (googleEventId: string) => {
      const headers = await authHeaders();
      return dismissEvt({ data: { googleEventId }, headers });
    },
    onSuccess: () => {
      toast.success("Event dismissed");
      invalidateEvents();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to dismiss"),
  });
  const addClientMut = useMutation({
    mutationFn: async (vars: {
      googleEventId: string;
      name: string;
      email: string;
      phone: string | null;
    }) => {
      const headers = await authHeaders();
      return addClientEvt({ data: vars, headers });
    },
    onSuccess: () => {
      toast.success("Client added and linked");
      invalidateEvents();
      qc.invalidateQueries({ queryKey: ["coach-students"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to add client"),
  });

  // Persist view
  useEffect(() => {
    try {
      const stored = localStorage.getItem(VIEW_STORAGE_KEY) as ScheduleView | null;
      if (stored === "week" || stored === "list") setView(stored);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
      // ignore
    }
  }, [view]);

  // Tick "now" every 60s for the live indicator
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Fetch a window wide enough to navigate +/- 2 weeks
  const eventsQ = useQuery({
    queryKey: ["calendar-events", 28],
    queryFn: async () => {
      const headers = await authHeaders();
      return (await fetchEvents({ data: { days: 28 }, headers })) as
        | { connected: true; events: UpcomingEvent[]; error?: string }
        | { connected: false; error: string };
    },
    staleTime: 5 * 60 * 1000,
  });

  const locsQ = useQuery({
    queryKey: ["coach-locations"],
    queryFn: async () => {
      const headers = await authHeaders();
      return (await fetchLocs({ headers })) as Array<{
        id: string;
        name: string;
        indoor: boolean;
        latitude: number | null;
      }>;
    },
    staleTime: 5 * 60 * 1000,
  });

  const forecastsQ = useQuery({
    queryKey: ["lesson-forecasts", 28],
    queryFn: async () => {
      const headers = await authHeaders();
      return (await fetchForecasts({ data: { days: 28 }, headers })) as ForecastLite[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // CoachQuick booking-page reservations (shown alongside Google events)
  const bookingsQ = useQuery({
    queryKey: ["calendar-bookings", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const from = new Date();
      from.setDate(from.getDate() - 14);
      const { data, error } = await supabase
        .from("coach_bookings")
        .select(
          "id, location_id, coach_student_id, session_id, booking_date, start_time, end_time, booker_name, booker_email",
        )
        .eq("coach_id", user!.id)
        .eq("status", "confirmed")
        .gte("booking_date", from.toISOString().slice(0, 10))
        .order("booking_date");
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        location_id: string;
        coach_student_id: string | null;
        session_id: string | null;
        booking_date: string;
        start_time: string;
        end_time: string;
        booker_name: string;
        booker_email: string;
      }>;
    },
    staleTime: 60 * 1000,
  });

  // Published CoachQuick clinics (group events), with seat counts
  const clinicsQ = useQuery({
    queryKey: ["calendar-clinics", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const from = new Date();
      from.setDate(from.getDate() - 14);
      const { data, error } = await supabase
        .from("clinics")
        .select(
          "id, title, clinic_date, start_time, end_time, location_id, capacity, status, clinic_registrations(status)",
        )
        .eq("coach_id", user!.id)
        .eq("status", "published")
        .gte("clinic_date", from.toISOString().slice(0, 10))
        .order("clinic_date");
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        title: string;
        clinic_date: string;
        start_time: string;
        end_time: string;
        location_id: string | null;
        capacity: number;
        status: string;
        clinic_registrations: { status: string }[];
      }>;
    },
    staleTime: 60 * 1000,
  });

  const locations = locsQ.data ?? [];
  const events = eventsQ.data?.connected ? (eventsQ.data.events ?? []) : [];
  const forecastByEvent = useMemo(
    () =>
      new Map(
        (forecastsQ.data ?? [])
          .filter((f) => f.google_event_id)
          .map((f) => [f.google_event_id!, f]),
      ),
    [forecastsQ.data],
  );
  const locById = useMemo(() => new Map(locations.map((l) => [l.id, l])), [locations]);

  // Reshape into ScheduleEvent[]
  const scheduleEvents = useMemo<ScheduleEvent[]>(() => {
    const today = new Date();
    return events
      .filter((ev) => ev.bucket !== "solo")
      .map((ev) => {
        const start = new Date(ev.start);
        const end = new Date(ev.end);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
        const startHr = fractionalHour(start);
        const endHr = fractionalHour(end);
        const durationMin = Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000));
        const loc = ev.locationId ? (locById.get(ev.locationId) ?? null) : null;
        const courtFull = loc?.name ?? "";
        const court = locShort(courtFull);
        const isNow = start.getTime() <= today.getTime() && today.getTime() <= end.getTime();
        const isDone = ev.bucket === "logged" || end.getTime() < today.getTime();
        return {
          id: ev.id,
          source: ev,
          day: dayIndex(start),
          startHr,
          endHr,
          durationMin,
          startDate: start,
          endDate: end,
          clientId: ev.matchedClient?.id ?? null,
          clientName: nameFromEvent(ev),
          clientEmail: ev.matchedClient?.email ?? ev.attendee?.email ?? null,
          sessionId: ev.sessionId,
          bucket: ev.bucket,
          court,
          skills: null,
          now: isNow,
          done: isDone,
          needsReview: ev.bucket === "candidate",
          attendeeName: ev.attendee?.name ?? null,
          attendeeEmail: ev.attendee?.email ?? null,
          attendeePhone: ev.attendee?.phone ?? null,
          isCalendly: ev.isCalendly,
          title: ev.title,
        } as ScheduleEvent;
      })
      .filter((x): x is ScheduleEvent => x !== null);
  }, [events, locById, now]); // include now to refresh now/done flags on tick

  // Reshape CoachQuick bookings into ScheduleEvent[] (coach-local wall time)
  const bookingEvents = useMemo<ScheduleEvent[]>(() => {
    const today = new Date();
    return (bookingsQ.data ?? []).map((b) => {
      const [y, mo, d] = b.booking_date.split("-").map(Number);
      const [sh, sm] = b.start_time.split(":").map(Number);
      const [eh, em] = b.end_time.split(":").map(Number);
      const start = new Date(y, mo - 1, d, sh, sm);
      const end = new Date(y, mo - 1, d, eh, em);
      const loc = locById.get(b.location_id) ?? null;
      const isNow = start.getTime() <= today.getTime() && today.getTime() <= end.getTime();
      const source: UpcomingEvent = {
        id: b.id,
        title: `Lesson — ${b.booker_name}`,
        start: start.toISOString(),
        end: end.toISOString(),
        startDate: b.booking_date,
        bucket: b.session_id ? "logged" : "matched",
        matchedClient: b.coach_student_id
          ? { id: b.coach_student_id, name: b.booker_name, email: b.booker_email }
          : null,
        attendee: null,
        sessionId: b.session_id,
        isCalendly: false,
        locationId: b.location_id,
      };
      return {
        id: b.id,
        source,
        day: dayIndex(start),
        startHr: fractionalHour(start),
        endHr: fractionalHour(end),
        durationMin: Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000)),
        startDate: start,
        endDate: end,
        clientId: b.coach_student_id,
        clientName: b.booker_name,
        clientEmail: b.booker_email,
        sessionId: b.session_id,
        bucket: source.bucket,
        court: locShort(loc?.name ?? ""),
        skills: null,
        now: isNow,
        done: !!b.session_id || end.getTime() < today.getTime(),
        needsReview: false,
        attendeeName: null,
        attendeeEmail: null,
        attendeePhone: null,
        isCalendly: false,
        title: source.title,
        isBooking: true,
      } as ScheduleEvent;
    });
  }, [bookingsQ.data, locById, now]);

  // Reshape clinics into ScheduleEvent[] (coach-local wall time, like bookings)
  const clinicEvents = useMemo<ScheduleEvent[]>(() => {
    const today = new Date();
    return (clinicsQ.data ?? []).map((c) => {
      const [y, mo, d] = c.clinic_date.split("-").map(Number);
      const [sh, sm] = c.start_time.split(":").map(Number);
      const [eh, em] = c.end_time.split(":").map(Number);
      const start = new Date(y, mo - 1, d, sh, sm);
      const end = new Date(y, mo - 1, d, eh, em);
      const loc = c.location_id ? (locById.get(c.location_id) ?? null) : null;
      const active = c.clinic_registrations.filter(
        (r) => r.status === "registered" || r.status === "pending_payment",
      ).length;
      const title = `Clinic — ${c.title} (${active}/${c.capacity})`;
      const source: UpcomingEvent = {
        id: c.id,
        title,
        start: start.toISOString(),
        end: end.toISOString(),
        startDate: c.clinic_date,
        bucket: "matched",
        matchedClient: null,
        attendee: null,
        sessionId: null,
        isCalendly: false,
        locationId: c.location_id,
      };
      return {
        id: c.id,
        source,
        day: dayIndex(start),
        startHr: fractionalHour(start),
        endHr: fractionalHour(end),
        durationMin: Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000)),
        startDate: start,
        endDate: end,
        clientId: null,
        clientName: title,
        clientEmail: null,
        sessionId: null,
        bucket: "matched",
        court: locShort(loc?.name ?? ""),
        skills: null,
        now: start.getTime() <= today.getTime() && today.getTime() <= end.getTime(),
        done: end.getTime() < today.getTime(),
        needsReview: false,
        attendeeName: null,
        attendeeEmail: null,
        attendeePhone: null,
        isCalendly: false,
        title,
        isClinic: true,
        clinicId: c.id,
      } as ScheduleEvent;
    });
  }, [clinicsQ.data, locById, now]);

  const allEvents = useMemo(
    () => [...scheduleEvents, ...bookingEvents, ...clinicEvents],
    [scheduleEvents, bookingEvents, clinicEvents],
  );

  const weekStart = useMemo(() => startOfWeek(new Date(), weekOffset), [weekOffset]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const weekEvents = useMemo(() => {
    const weekIsoStart = isoDay(weekDays[0]);
    const weekIsoEnd = isoDay(weekDays[6]);
    return allEvents.filter((e) => {
      const iso = isoDay(e.startDate);
      return iso >= weekIsoStart && iso <= weekIsoEnd;
    });
  }, [allEvents, weekDays]);

  // Forecast for a given day (use first event's forecast that day)
  const dayForecast = useMemo(() => {
    const m = new Map<string, ForecastLite | null>();
    for (const d of weekDays) m.set(isoDay(d), null);
    for (const e of weekEvents) {
      const iso = isoDay(e.startDate);
      const cur = m.get(iso);
      if (!cur) {
        const f = forecastByEvent.get(e.id);
        if (f) m.set(iso, f);
      }
    }
    return m;
  }, [weekEvents, forecastByEvent, weekDays]);

  // Header summary
  const lessonsBooked = weekEvents.filter((e) => !e.needsReview).length;
  const totalMinutes = weekEvents.reduce((s, e) => s + e.durationMin, 0);
  const openSlots = Math.max(0, 20 - lessonsBooked);
  const weekLabel = weekDays[0].toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
  });

  const isCurrentWeek = isoDay(weekDays[0]) === isoDay(startOfWeek(new Date(), 0));

  async function handleConnect() {
    try {
      const headers = await authHeaders();
      const { url } = await fetchAuthUrl({ headers });
      window.location.href = url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to start Google sign-in");
    }
  }

  function eventActions(ev: ScheduleEvent) {
    function logSession() {
      if (!ev.clientId) {
        toast.warning("Match this event to a client first (in Manage external events).");
        return;
      }
      nav({
        to: "/app/log",
        search: {
          clientId: ev.clientId,
          date: ev.source.startDate || undefined,
          // Booking-page reservations aren't Google events — don't link them.
          calendarEventId: ev.isBooking ? undefined : ev.id,
        },
      });
    }
    function openRecap() {
      if (ev.sessionId)
        nav({ to: "/app/sessions/$sessionId", params: { sessionId: ev.sessionId } });
      else toast.info("No recap yet — log this session first.");
    }
    function reschedule() {
      if (ev.isBooking) {
        nav({ to: "/app/bookings" });
        return;
      }
      toast.info("Reschedule from Google Calendar to keep the event in sync.");
    }
    function addClient() {
      setReviewOpen({ ev, tab: "create" });
    }
    function linkExisting() {
      setReviewOpen({ ev, tab: "link" });
    }
    function dismiss() {
      dismissMut.mutate(ev.id);
    }
    function cancelBooking() {
      if (ev.isBooking) setCancelTarget(ev);
    }
    return { logSession, openRecap, reschedule, addClient, linkExisting, dismiss, cancelBooking };
  }

  if (eventsQ.isLoading) {
    return (
      <AppShell>
        <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
          <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading schedule…
          </div>
        </div>
      </AppShell>
    );
  }

  // Google not connected is no longer a full-page gate — the calendar always
  // renders (CoachQuick bookings work without Google); we show a banner instead.
  const googleNotConnected = !!eventsQ.data && !eventsQ.data.connected;
  const googleNeedsReauth =
    googleNotConnected &&
    (eventsQ.data as { connected: false; error: string }).error === "reauth_required";

  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Header — title + page-level tabs (Calendar / Booking page) */}
        <div className="mb-5">
          <h1 className="font-display text-4xl font-black tracking-tight text-foreground md:text-5xl">
            Schedule
          </h1>
          <div className="mt-4">
            <ScheduleTabs />
          </div>
        </div>

        {/* Calendar-tab controls: week nav, view toggle, week summary */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Week of {weekLabel}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {lessonsBooked} lesson{lessonsBooked === 1 ? "" : "s"} booked ·{" "}
              {(totalMinutes / 60).toFixed(1).replace(/\.0$/, "")} hours on court · {openSlots} open
              slots
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center rounded-[10px] border bg-card p-0.5">
              <button
                type="button"
                onClick={() => setWeekOffset((o) => o - 1)}
                className="flex h-8 w-8 items-center justify-center rounded-[8px] text-muted-foreground hover:text-foreground"
                aria-label="Previous week"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setWeekOffset(0)}
                disabled={isCurrentWeek}
                className={`h-8 rounded-[8px] px-3 text-xs font-semibold transition ${
                  isCurrentWeek
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setWeekOffset((o) => o + 1)}
                className="flex h-8 w-8 items-center justify-center rounded-[8px] text-muted-foreground hover:text-foreground"
                aria-label="Next week"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <ToggleGroup
              type="single"
              value={view}
              onValueChange={(v) => v && setView(v as ScheduleView)}
              className="rounded-[10px] border bg-card p-0.5"
              aria-label="Schedule view"
            >
              <ToggleGroupItem
                value="week"
                aria-label="Week view"
                className="h-8 gap-1.5 rounded-[8px] px-2.5 text-xs font-semibold data-[state=on]:bg-foreground data-[state=on]:text-background"
              >
                <LayoutGrid className="h-3.5 w-3.5" /> Week
              </ToggleGroupItem>
              <ToggleGroupItem
                value="list"
                aria-label="List view"
                className="h-8 gap-1.5 rounded-[8px] px-2.5 text-xs font-semibold data-[state=on]:bg-foreground data-[state=on]:text-background"
              >
                <Rows3 className="h-3.5 w-3.5" /> List
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        {googleNotConnected && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-dashed bg-card px-4 py-3 text-sm">
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">
                {googleNeedsReauth
                  ? "Google Calendar connection expired."
                  : "Google Calendar not connected."}
              </span>{" "}
              {googleNeedsReauth
                ? "Reconnect to keep seeing outside events here."
                : "Optional — connect it to also see your Google Calendar events here. CoachQuick bookings already show below."}
            </p>
            {googleNeedsReauth ? (
              <Button size="sm" onClick={handleConnect}>
                Reconnect
              </Button>
            ) : (
              <Link
                to="/app/settings"
                className="text-sm font-semibold text-brand-text hover:underline"
              >
                Connect in Settings →
              </Link>
            )}
          </div>
        )}

        <div className="mb-3 flex items-center gap-4 text-[11px] font-medium text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-[#9fe870]" /> Google Calendar
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-[#7dd3fc]" /> Booked on CoachQuick
          </span>
        </div>

        {view === "week" ? (
          <WeekGrid
            weekDays={weekDays}
            isCurrentWeek={isCurrentWeek}
            events={weekEvents}
            forecasts={dayForecast}
            now={now}
            actions={eventActions}
            onAddLesson={(date, start) => setBookingSeed({ date, start })}
          />
        ) : (
          <ListView
            weekDays={weekDays}
            events={weekEvents}
            forecasts={dayForecast}
            now={now}
            actions={eventActions}
            onAddLesson={(date) => setBookingSeed({ date })}
          />
        )}
      </div>
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget
                ? `${cancelTarget.clientName}'s ${fmtTimeOfDay(cancelTarget.startDate)} lesson on ${cancelTarget.startDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} will be cancelled and the slot reopens on your booking page. Heads up: the client isn't emailed automatically yet — let them know.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep booking</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (cancelTarget) cancelBookingMut.mutate(cancelTarget.id);
                setCancelTarget(null);
              }}
            >
              Yes, cancel it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReviewEventDialog
        state={reviewOpen}
        onOpenChange={(o) => !o && setReviewOpen(null)}
        onTabChange={(tab) => setReviewOpen((s) => (s ? { ...s, tab } : s))}
        coachId={user?.id ?? null}
        busy={addClientMut.isPending || linkMut.isPending}
        onCreate={(vars) => addClientMut.mutate(vars, { onSuccess: () => setReviewOpen(null) })}
        onLink={(vars) => linkMut.mutate(vars, { onSuccess: () => setReviewOpen(null) })}
      />
      <NewBookingDialog
        seed={bookingSeed}
        onOpenChange={(o) => !o && setBookingSeed(null)}
        coachId={user?.id ?? null}
        locations={locations.map((l) => ({ id: l.id, name: l.name }))}
      />
    </AppShell>
  );
}

function DayWeather({ forecast }: { forecast: ForecastLite | null }) {
  if (!forecast) return <span className="h-4 w-4 rounded-full bg-muted/50" aria-hidden />;
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <WeatherIcon
        weatherCode={forecast.weather_code}
        severity={forecast.severity}
        tempF={forecast.temp_f}
        precipProb={forecast.precip_prob}
        windMph={forecast.wind_mph}
        reason={forecast.flag_reason}
        alertEvent={forecast.severe_alert?.event ?? null}
      />
      {forecast.temp_f != null && (
        <span className="font-semibold">{Math.round(forecast.temp_f)}°</span>
      )}
    </div>
  );
}

function WeekGrid({
  weekDays,
  isCurrentWeek,
  events,
  forecasts,
  now,
  actions,
  onAddLesson,
}: {
  weekDays: Date[];
  isCurrentWeek: boolean;
  events: ScheduleEvent[];
  forecasts: Map<string, ForecastLite | null>;
  now: Date;
  actions: (ev: ScheduleEvent) => EventActions;
  onAddLesson: (isoDate: string, startHHMM?: string) => void;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventsByDay = useMemo(() => {
    const m = new Map<number, ScheduleEvent[]>();
    for (const e of events) {
      const arr = m.get(e.day) ?? [];
      arr.push(e);
      m.set(e.day, arr);
    }
    return m;
  }, [events]);

  const currentHour = fractionalHour(now);
  const showNowLine = isCurrentWeek && currentHour >= HOUR_START && currentHour <= HOUR_END + 1;
  const todayDay = isCurrentWeek ? dayIndex(now) : -1;

  return (
    <div className="overflow-hidden rounded-[16px] border bg-card">
      <div className="grid grid-cols-[60px_repeat(7,1fr)]">
        {/* Top-left corner */}
        <div className="border-b border-r" />
        {/* Day headers */}
        {weekDays.map((d, i) => {
          const iso = isoDay(d);
          const isToday = iso === isoDay(today);
          const forecast = forecasts.get(iso) ?? null;
          return (
            <div
              key={iso}
              className={`flex items-center justify-between gap-2 border-b border-r px-3 py-2.5 last:border-r-0 ${
                isToday ? "bg-[#9fe870]/10" : ""
              }`}
            >
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {DAY_LABELS[i]}
                </p>
                <p className="font-display text-2xl font-black leading-none tracking-tight text-foreground">
                  {d.getDate()}
                </p>
              </div>
              <DayWeather forecast={forecast} />
            </div>
          );
        })}

        {/* Hours + columns */}
        <div className="col-span-8 grid grid-cols-[60px_repeat(7,1fr)]">
          {/* Hour gutter */}
          <div className="border-r">
            {HOURS.map((hr) => (
              <div
                key={hr}
                className="flex items-start justify-end pr-2 pt-1 text-[10px] font-semibold text-muted-foreground"
                style={{ height: HOUR_HEIGHT }}
              >
                {fmtHourLabel(hr)}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((d, dayIdx) => {
            const iso = isoDay(d);
            const isToday = iso === isoDay(today);
            const dayEvents = eventsByDay.get(dayIdx) ?? [];
            return (
              <div
                key={iso}
                className={`relative border-r last:border-r-0 ${isToday ? "bg-[#9fe870]/10" : ""}`}
                style={{ height: HOUR_HEIGHT * HOURS.length }}
              >
                {/* Hour gridlines — clicking an empty one books a lesson */}
                {HOURS.map((hr) => (
                  <button
                    key={hr}
                    type="button"
                    title={`Book a lesson at ${fmtHourLabel(hr)}`}
                    onClick={() => onAddLesson(iso, `${String(hr).padStart(2, "0")}:00`)}
                    className="group block w-full border-b border-border/60 text-left hover:bg-foreground/[0.04]"
                    style={{ height: HOUR_HEIGHT }}
                  >
                    <span className="pointer-events-none flex h-full items-center justify-center text-[11px] font-semibold text-muted-foreground opacity-0 transition group-hover:opacity-100">
                      + Add
                    </span>
                  </button>
                ))}

                {/* Now indicator */}
                {showNowLine && dayIdx === todayDay && (
                  <div
                    className="pointer-events-none absolute left-0 right-0 z-20"
                    style={{ top: (currentHour - HOUR_START) * HOUR_HEIGHT }}
                  >
                    <div className="relative">
                      <span className="absolute -left-[5px] -top-[4px] block h-[10px] w-[10px] rounded-full bg-[#9fe870] shadow-[0_0_0_3px_rgba(159,232,112,0.25)]" />
                      <span className="block h-[2px] w-full bg-[#9fe870]" />
                    </div>
                  </div>
                )}

                {/* Events */}
                {dayEvents.map((e) => {
                  const top = (e.startHr - HOUR_START) * HOUR_HEIGHT;
                  const height = Math.max(
                    28,
                    (Math.min(e.endHr, HOUR_END + 1) - e.startHr) * HOUR_HEIGHT - 2,
                  );
                  const isInProgress = e.now;
                  const isDone = e.done && !isInProgress;
                  return (
                    <WeekBlock
                      key={e.id}
                      ev={e}
                      top={top}
                      height={height}
                      inProgress={isInProgress}
                      done={isDone}
                      actions={actions}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WeekBlock({
  ev,
  top,
  height,
  inProgress,
  done,
  actions,
}: {
  ev: ScheduleEvent;
  top: number;
  height: number;
  inProgress: boolean;
  done: boolean;
  actions: (ev: ScheduleEvent) => EventActions;
}) {
  const handlers = actions(ev);
  const onClick = ev.needsReview
    ? handlers.addClient
    : inProgress
      ? handlers.logSession
      : done
        ? handlers.openRecap
        : handlers.logSession;
  const block = (
    <button
      type="button"
      onClick={ev.isBooking ? undefined : onClick}
      className={`absolute left-1 right-1 z-10 overflow-hidden rounded-[6px] border px-2 py-1.5 text-left text-xs transition ${
        inProgress
          ? "border-[#0e0f0c] bg-[#0e0f0c] text-white shadow-[0_2px_8px_-2px_rgba(14,15,12,0.4)]"
          : done
            ? "border-l-2 border-l-border bg-card opacity-55"
            : ev.isBooking
              ? "border-l-[3px] border-l-[#7dd3fc] border-y-border border-r-border bg-card hover:shadow-soft"
              : "border-l-[3px] border-l-[#9fe870] border-y-border border-r-border bg-card hover:shadow-soft"
      }`}
      style={{ top, height }}
      title={`${ev.clientName} · ${fmtTimeOfDay(ev.startDate)}`}
    >
      <p className={`truncate font-bold ${inProgress ? "text-white" : "text-foreground"}`}>
        {ev.clientName}
      </p>
      <p
        className={`truncate text-[11px] ${inProgress ? "text-white/80" : "text-muted-foreground"}`}
      >
        {ev.court || fmtTimeOfDay(ev.startDate)}
        {inProgress && " · now"}
      </p>
    </button>
  );

  // Clinics open a small menu that routes to the roster page.
  if (ev.isClinic) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{block}</DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <div className="px-2 py-1.5 text-xs">
            <p className="font-semibold">{ev.title}</p>
            <p className="text-muted-foreground">
              {fmtTimeOfDay(ev.startDate)} – {fmtTimeOfDay(ev.endDate)}
              {ev.court ? ` · ${ev.court}` : ""}
            </p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/app/clinics/$clinicId" params={{ clinicId: ev.clinicId! }}>
              View roster
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // CoachQuick bookings open a details menu instead of jumping straight away.
  if (ev.isBooking) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{block}</DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <div className="px-2 py-1.5 text-xs">
            <p className="font-semibold">{ev.clientName}</p>
            <p className="text-muted-foreground">
              {fmtTimeOfDay(ev.startDate)} – {fmtTimeOfDay(ev.endDate)}
              {ev.court ? ` · ${ev.court}` : ""}
            </p>
            {ev.clientEmail && <p className="text-muted-foreground">{ev.clientEmail}</p>}
          </div>
          <DropdownMenuSeparator />
          {ev.clientId && (
            <DropdownMenuItem asChild>
              <Link to="/app/clients/$clientId" params={{ clientId: ev.clientId }}>
                View client
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={handlers.logSession}>
            <Pencil className="h-3.5 w-3.5" /> Log session
          </DropdownMenuItem>
          {ev.sessionId && (
            <DropdownMenuItem onSelect={handlers.openRecap}>View recap</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={handlers.cancelBooking}
            className="text-destructive focus:text-destructive"
          >
            <X className="h-3.5 w-3.5" /> Cancel booking
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return block;
}

function ListView({
  weekDays,
  events,
  forecasts,
  now,
  actions,
  onAddLesson,
}: {
  weekDays: Date[];
  events: ScheduleEvent[];
  forecasts: Map<string, ForecastLite | null>;
  now: Date;
  actions: (ev: ScheduleEvent) => EventActions;
  onAddLesson: (isoDate: string) => void;
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventsByDay = useMemo(() => {
    const m = new Map<string, ScheduleEvent[]>();
    for (const e of events) {
      const iso = isoDay(e.startDate);
      const arr = m.get(iso) ?? [];
      arr.push(e);
      m.set(iso, arr);
    }
    for (const [, arr] of m) arr.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
    return m;
  }, [events]);

  return (
    <div className="overflow-hidden rounded-[16px] border bg-card">
      {weekDays.map((d, i) => {
        const iso = isoDay(d);
        const isToday = iso === isoDay(today);
        const forecast = forecasts.get(iso) ?? null;
        const dayEvents = eventsByDay.get(iso) ?? [];
        const totalMin = dayEvents.reduce((s, e) => s + e.durationMin, 0);
        const lessonCount = dayEvents.length;
        return (
          <div key={iso} className="border-b last:border-b-0">
            <div
              className={`sticky top-0 z-10 grid grid-cols-[120px_1fr_auto] items-center gap-4 border-b bg-card/90 px-5 py-3 backdrop-blur ${
                isToday ? "bg-[#9fe870]/10" : ""
              }`}
            >
              <div className="flex items-baseline gap-3">
                <p className="font-display text-[36px] font-black leading-none tracking-tight text-foreground">
                  {d.getDate()}
                </p>
                <div>
                  <p className="text-sm font-semibold leading-none">{DAY_FULL[i]}</p>
                  <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {d.toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                  </p>
                </div>
                {isToday && (
                  <span className="inline-flex items-center rounded-full bg-[#2ead4b]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#2ead4b]">
                    Today
                  </span>
                )}
              </div>
              <div className="text-center text-xs text-muted-foreground">
                {lessonCount === 0 ? (
                  <span className="italic">No lessons scheduled</span>
                ) : (
                  <>
                    {lessonCount} lesson{lessonCount === 1 ? "" : "s"} ·{" "}
                    {(totalMin / 60).toFixed(1).replace(/\.0$/, "")} hrs on court
                  </>
                )}
              </div>
              <div className="flex items-center gap-2 justify-self-end">
                <DayWeather forecast={forecast} />
                {dayEvents.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onAddLesson(iso)}
                    className="h-7 px-2 text-xs font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <Plus className="h-3 w-3" /> Add
                  </Button>
                )}
              </div>
            </div>

            {dayEvents.length === 0 ? (
              <div className="flex items-center justify-between gap-3 px-5 py-4">
                <span className="text-xs italic text-muted-foreground">Open day</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAddLesson(iso)}
                  className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                >
                  <Plus className="h-3 w-3" /> Add lesson
                </Button>
              </div>
            ) : (
              <ul>
                {dayEvents.map((e) => (
                  <ListRow key={e.id} ev={e} actions={actions} />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ListRow({
  ev,
  actions,
}: {
  ev: ScheduleEvent;
  actions: (ev: ScheduleEvent) => EventActions;
}) {
  const handlers = actions(ev);
  const inProgress = ev.now;
  const done = ev.done && !inProgress;
  return (
    <li
      className={`flex items-center gap-3 border-t border-border/60 px-5 py-[14px] first:border-t-0 ${
        inProgress ? "bg-[#0e0f0c]/[0.04] dark:bg-white/[0.03]" : ""
      } ${done ? "opacity-55" : ""}`}
    >
      <div className="w-[110px] shrink-0">
        <p className="text-sm font-bold">{fmtTimeOfDay(ev.startDate)}</p>
        <p className="text-xs text-muted-foreground">{fmtDuration(ev.durationMin)}</p>
      </div>

      <span
        className={`h-2 w-2 shrink-0 rounded-full ${
          inProgress
            ? "animate-pulse bg-[#9fe870]"
            : done
              ? "bg-border"
              : "bg-[#0e0f0c] dark:bg-foreground"
        }`}
        aria-hidden
      />

      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className={`text-[10px] font-bold ${paletteFor(ev.clientName)}`}>
          {initialsOf(ev.clientName)}
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {ev.clientId ? (
            <Link
              to="/app/clients/$clientId"
              params={{ clientId: ev.clientId }}
              className="text-sm font-semibold hover:underline"
            >
              {ev.clientName}
            </Link>
          ) : (
            <span className="text-sm font-semibold">{ev.clientName}</span>
          )}
          {inProgress && (
            <span className="inline-flex items-center rounded-full bg-[#9fe870]/30 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#054d28] dark:bg-[#9fe870]/20 dark:text-[#7ed492]">
              In progress
            </span>
          )}
          {done && !inProgress && (
            <span className="inline-flex items-center rounded-full bg-[#2ead4b]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#2ead4b]">
              Done
            </span>
          )}
          {ev.needsReview && (
            <span className="inline-flex items-center rounded-full bg-[#fff3c2] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#b86700] dark:bg-[#b86700]/20 dark:text-[#fcd34d]">
              Needs review
            </span>
          )}
          {ev.isBooking && (
            <span className="inline-flex items-center rounded-full bg-[#7dd3fc]/25 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#075985] dark:bg-[#7dd3fc]/15 dark:text-[#7dd3fc]">
              Booked online
            </span>
          )}
        </div>
        <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {ev.court && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {ev.court}
            </span>
          )}
          {ev.skills && <span>· Focus: {ev.skills}</span>}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {ev.needsReview ? (
          <Button size="sm" onClick={handlers.addClient} className="rounded-[8px]">
            <UserPlus className="h-3.5 w-3.5" /> Add client
          </Button>
        ) : inProgress ? (
          <Button size="sm" onClick={handlers.logSession} className="rounded-[8px]">
            <Pencil className="h-3.5 w-3.5" /> Log drills
          </Button>
        ) : done ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={handlers.openRecap}
            className="rounded-[8px] text-muted-foreground hover:text-foreground"
          >
            View recap
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={handlers.reschedule}
            className="rounded-[8px] text-muted-foreground hover:text-foreground"
          >
            {ev.isBooking ? "Manage" : "Reschedule"}
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Lesson actions"
              className="flex h-7 w-7 items-center justify-center rounded-[8px] border text-muted-foreground hover:text-foreground"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {ev.needsReview ? (
              <>
                <DropdownMenuItem onSelect={handlers.addClient}>
                  <UserPlus className="h-3.5 w-3.5" /> Add client from event
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handlers.linkExisting}>
                  <Link2 className="h-3.5 w-3.5" /> Link to existing client
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handlers.dismiss}>
                  <X className="h-3.5 w-3.5" /> Dismiss event
                </DropdownMenuItem>
              </>
            ) : (
              <>
                {ev.clientId && (
                  <DropdownMenuItem asChild>
                    <Link to="/app/clients/$clientId" params={{ clientId: ev.clientId }}>
                      View client
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={handlers.logSession}>
                  <Pencil className="h-3.5 w-3.5" /> Log session
                </DropdownMenuItem>
                {ev.sessionId && (
                  <DropdownMenuItem onSelect={handlers.openRecap}>View recap</DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                {ev.isBooking ? (
                  <DropdownMenuItem
                    onSelect={handlers.cancelBooking}
                    className="text-destructive focus:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" /> Cancel booking
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={handlers.reschedule}>Reschedule</DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}

type ReviewState = { ev: ScheduleEvent; tab: "create" | "link" } | null;

function ReviewEventDialog({
  state,
  onOpenChange,
  onTabChange,
  coachId,
  busy,
  onCreate,
  onLink,
}: {
  state: ReviewState;
  onOpenChange: (open: boolean) => void;
  onTabChange: (tab: "create" | "link") => void;
  coachId: string | null;
  busy: boolean;
  onCreate: (vars: {
    googleEventId: string;
    name: string;
    email: string;
    phone: string | null;
  }) => void;
  onLink: (vars: { googleEventId: string; clientId: string }) => void;
}) {
  const ev = state?.ev ?? null;
  const tab = state?.tab ?? "create";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [linkClientId, setLinkClientId] = useState<string | null>(null);

  useEffect(() => {
    if (!ev) return;
    setName(ev.attendeeName ?? "");
    setEmail(ev.attendeeEmail ?? "");
    setPhone(ev.attendeePhone ?? "");
    setLinkClientId(null);
  }, [ev]);

  function submit() {
    if (!ev) return;
    if (tab === "link") {
      if (!linkClientId) {
        toast.warning("Pick a client to link.");
        return;
      }
      onLink({ googleEventId: ev.id, clientId: linkClientId });
    } else {
      if (!name.trim() || !email.trim()) {
        toast.warning("Name and email are required.");
        return;
      }
      onCreate({
        googleEventId: ev.id,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
      });
    }
  }

  return (
    <Dialog open={!!state} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add client from event</DialogTitle>
          <DialogDescription>
            {ev?.title}
            {ev?.isCalendly ? " · via Calendly" : ""}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={(v) => onTabChange(v as "create" | "link")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="create">Create new</TabsTrigger>
            <TabsTrigger value="link">Link existing</TabsTrigger>
          </TabsList>
          <TabsContent value="create" className="space-y-3 pt-3">
            <div>
              <Label htmlFor="rev-name">Name</Label>
              <Input id="rev-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="rev-email">Email</Label>
              <Input
                id="rev-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {!ev?.attendeeEmail && (
                <p className="mt-1 text-xs text-muted-foreground">
                  No email on this event — add one to send recaps.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="rev-phone">Phone (optional)</Label>
              <Input id="rev-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </TabsContent>
          <TabsContent value="link" className="space-y-3 pt-3">
            <p className="text-xs text-muted-foreground">
              Link this event to a client that already exists in your roster.
            </p>
            {coachId && (
              <div>
                <Label className="text-xs">Pick a client</Label>
                <div className="mt-1">
                  <ClientPicker
                    coachId={coachId}
                    value={linkClientId}
                    onChange={setLinkClientId}
                    placeholder="Pick a client…"
                    allowNone={false}
                  />
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {tab === "link" ? "Link client" : "Add client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const Route = createFileRoute("/app/calendar")({
  head: () => ({
    meta: buildAppMeta({
      title: "Calendar — CoachQuick",
      description:
        "Your coaching calendar with upcoming sessions, Google Calendar sync, and quick session logging.",
      path: "/app/calendar",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/calendar` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <CalendarPage />
    </RequireAuth>
  ),
});
